import React, { useState } from "react";
import style from "./styles.module.scss";
import { Table, Button, Row, Col, Input } from "reactstrap";
import { Combobox, Field } from "../../Order/Field/Field";
import { Mode } from "../../../Asset/types";
import { Numeric } from "../../Order/Field/Field";
import { FormattedNumber } from "../../../FormattedNumber/FormattedNumber";
import { showToast } from "../../../../utils/Helper";

const emptyItem = {
  CustodyRateRangesPositiveRate: null,
  CustodyRateRangesNegativeRate: null,
  CustodyRateRangesMax: null,
  CustodyRateRangesMin: null,
};

const CurrencyList = ({ onChange, loadings, items, currencies, imputableAccounts, custody, errors }) => {
  const [edit, setEdit] = useState({
    open: false,
    mode: Mode.Insert,
    fields: {},
    errors: {},
  });

  const openEdit = (item) => {
    if (item) {
      setEdit({
        open: true,
        mode: Mode.Update,
        fields: { ...item },
        errors: {},
      });
    } else {
      setEdit({
        open: true,
        mode: Mode.Insert,
        fields: { ...emptyItem },
        errors: {},
      });
    }
  };

  const closeEdit = ({ confirm }) => {
    if (confirm) {
      if (validateRanges(edit.fields)) {
        if (edit.mode === Mode.Insert) {
          handleAction(Mode.Insert);
        } else {
          handleAction(Mode.Update);
        }
      } else {
        showToast("The ranges are not consistent. Please check the values.", true);
      }
    }

    setEdit({});
  };

  const validateRanges = (newRange) => {
    if (newRange.CustodyRateRangesMin >= newRange.CustodyRateRangesMax) return false;
    for (let item of items) {
      if (
        item.CustodyRateRangesLineId !== newRange.CustodyRateRangesLineId &&
        ((newRange.CustodyRateRangesMin <= item.CustodyRateRangesMax &&
          newRange.CustodyRateRangesMin >= item.CustodyRateRangesMin) ||
          (newRange.CustodyRateRangesMax <= item.CustodyRateRangesMax &&
            newRange.CustodyRateRangesMax >= item.CustodyRateRangesMin))
      ) {
        return false;
      }
    }
    return true;
  };

  const handleAction = (action, item) => {
    let updatedItems = [];

    if (action === Mode.Delete) {
      updatedItems = items.filter((it) => it.CustodyRateRangesLineId !== item.CustodyRateRangesLineId);
    } else {
      if (action === Mode.Insert) {
        let maxCustodyRateRangesLineIdItem = items.reduce((maxItem, currentItem) => {
          return currentItem.CustodyRateRangesLineId > maxItem.CustodyRateRangesLineId
            ? currentItem
            : maxItem;
        }, items[0]);

        let newFields = edit.fields;
        newFields.CustodyRateRangesLineId = maxCustodyRateRangesLineIdItem
          ? parseInt(maxCustodyRateRangesLineIdItem.CustodyRateRangesLineId) + 1
          : 1;

        updatedItems = [...items, newFields];
      } else if (action === Mode.Update) {
        updatedItems = items.map((item) => {
          if (item.CustodyRateRangesLineId === edit.fields.CustodyRateRangesLineId) {
            return edit.fields;
          }
          return item;
        });
      }
    }

    updatedItems.sort((a, b) => a.CustodyRateRangesMin - b.CustodyRateRangesMin);

    onChange({ rateRanges: updatedItems });
  };

  const handleOnChangeEdit = (fields) => {
    setEdit({ ...edit, fields: { ...edit.fields, ...fields } });
  };

  if (loadings.imputableAccounts || loadings.currencies) {
    return null;
  }

  return (
    <div>
      <div className="text-right">
        <Button color="primary" size="sm" className="mb-2" onClick={() => openEdit()} disabled={!!edit.open}>
          <i className="fa fa-plus mr-2" />
          Add new
        </Button>
      </div>
      {edit.open && (
        <Edit
          mode={edit.mode}
          item={edit.fields}
          errors={edit.errors}
          currencies={currencies}
          imputableAccounts={imputableAccounts}
          onChange={handleOnChangeEdit}
          toggle={closeEdit}
          custody={custody}
        />
      )}
      {!edit.open && (
        <div className={style.table}>
          <Table responsive striped>
            <thead>
              <tr>
                <th>Positive Rate</th>
                <th>Negative Rate</th>
                <th>Money Market Rate Min</th>
                <th>Money Market Rate Max</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.length > 0 &&
                items.map((item, index) => (
                  <tr key={`${item.CustodyRateRangesLineId}`}>
                    <td>
                      <FormattedNumber
                        value={item.CustodyRateRangesPositiveRate || 0}
                        dataTypeDecimalName="percentage"
                      />
                      {"%"}
                    </td>
                    <td>
                      <FormattedNumber
                        value={item.CustodyRateRangesNegativeRate || 0}
                        dataTypeDecimalName="percentage"
                      />
                      {"%"}
                    </td>
                    <td>
                      <FormattedNumber
                        value={item.CustodyRateRangesMin || 0}
                        dataTypeDecimalName="currency"
                      />{" "}
                    </td>
                    <td>
                      {" "}
                      <FormattedNumber
                        value={item.CustodyRateRangesMax || 0}
                        dataTypeDecimalName="currency"
                      />{" "}
                    </td>
                    <td style={{ minWidth: 80 }}>
                      <Button color="btn-outline-primary btn-sm" onClick={() => openEdit(item)}>
                        <i className="fa fa-pencil" />
                      </Button>
                      <Button
                        color="btn-outline-primary btn-sm"
                        style={{ color: "#d83737" }}
                        onClick={() => handleAction(Mode.Delete, item)}
                      >
                        <i className="fa fa-trash" />
                      </Button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

const Edit = ({ mode, item, errors, currencies, imputableAccounts, onChange, toggle, custody }) => {
  return (
    <div className={style.edit}>
      <span className="d-block text-primary mb-3" style={{ fontWeight: 500 }}>
        {mode === Mode.Insert ? "Adding new row" : "Editing row"}
      </span>
      <Row>
        <Col lg={4} xs={12}>
          <Field label={"Positive Rate"} error={errors.CustodyRateRangesPositiveRate}>
            <Numeric
              dataTypeDecimalName="quantity"
              placeholder={"Positive Rate"}
              value={item.CustodyRateRangesPositiveRate || ""}
              invalid={errors.CustodyRateRangesPositiveRate}
              onChange={(e) => onChange({ CustodyRateRangesPositiveRate: e })}
            />
          </Field>
        </Col>
        <Col lg={4} xs={12}>
          <Field label={"Negative Rate"} error={errors.CustodyRateRangesNegativeRate}>
            <Numeric
              dataTypeDecimalName="quantity"
              placeholder={"Negative Rate"}
              value={item.CustodyRateRangesNegativeRate || ""}
              invalid={errors.CustodyRateRangesNegativeRate}
              onChange={(e) => onChange({ CustodyRateRangesNegativeRate: e })}
            />
          </Field>
        </Col>
        <Col lg={4} xs={12}>
          <Field label={"Money Market Rate Min"} error={errors.CustodyRateRangesMin}>
            <Numeric
              dataTypeDecimalName="quantity"
              placeholder={"Money Market Rate Min"}
              value={item.CustodyRateRangesMin || ""}
              invalid={errors.CustodyRateRangesMin}
              onChange={(e) => onChange({ CustodyRateRangesMin: e })}
            />
          </Field>
        </Col>
        <Col lg={4} xs={12}>
          <Field label={"Money Market Rate Max"} error={errors.CustodyRateRangesMax}>
            <Numeric
              dataTypeDecimalName="quantity"
              placeholder={"Money Market Rate Max"}
              value={item.CustodyRateRangesMax || ""}
              invalid={errors.CustodyRateRangesMax}
              onChange={(e) => onChange({ CustodyRateRangesMax: e })}
            />
          </Field>
        </Col>
      </Row>
      <Row>
        <Col lg="7">
          <div className={style.buttons}>
            <Button size="sm" color="light" className="mr-2" onClick={() => toggle({})}>
              Cancel
            </Button>
            <Button size="sm" color="primary" onClick={() => toggle({ confirm: true })}>
              Confirm
            </Button>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default CurrencyList;
